import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// localStorage key used for the Bearer-token fallback. Cookies remain the
// primary auth mechanism; this is only used when browsers block 3rd-party
// cookies (Safari ITP, Chrome phaseout, incognito, etc.).
export const ACCESS_TOKEN_KEY = "bt_access_token";

export function setStoredAccessToken(token) {
  if (token) {
    try { window.localStorage.setItem(ACCESS_TOKEN_KEY, token); } catch (_) { /* ignore */ }
  }
}
export function clearStoredAccessToken() {
  try { window.localStorage.removeItem(ACCESS_TOKEN_KEY); } catch (_) { /* ignore */ }
}
export function getStoredAccessToken() {
  try { return window.localStorage.getItem(ACCESS_TOKEN_KEY); } catch (_) { return null; }
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Authorization: Bearer <token> from localStorage if present.
// Does not overwrite a header that was set explicitly on the request.
api.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Iter 70 — DO NOT auto-clear the stored token on a 401. That pattern
// produced the production "login → 200 OK → /auth/me 401 loop" bug: the
// first stale 401 after login (cookie not yet propagated cross-origin,
// or an in-flight request from the pre-login state) wiped the token
// from localStorage, leaving every subsequent request unauthenticated
// with no recovery path other than a manual cache clear.
//
// Tokens are now ONLY cleared on explicit logout (AuthContext.logout)
// or when the JWT proves expired/invalid via the backend's reasoned
// 401 detail. Sending a stale token by accident is harmless — the
// backend will simply 401 again until the user retries / refreshes.
api.interceptors.response.use(
  (resp) => resp,
  (error) => Promise.reject(error),
);

export default api;

// ────────────────────────────────────────────────────────────────────
// Iter 28 — Cold-start safe POST with automatic retries.
//
// Cloudflare's "could not parse response" surfaces when the origin is cold
// and returns mid-stream / times out before headers flush. We treat anything
// that ISN'T a clean 4xx response as a transient network error and retry up
// to `retries` times with linear backoff. 401/400/etc. are NEVER retried
// (wrong password should fail fast).
// ────────────────────────────────────────────────────────────────────
function _isRetryable(error) {
  // Network error / no response from server — retry.
  if (!error.response) return true;
  const s = error.response.status;
  // 5xx / 502 / 503 / 504 → retry. 4xx are real errors → no retry.
  return s >= 500 && s < 600;
}

export async function postWithRetry(url, body, { retries = 2, backoffMs = 600, onRetry } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await api.post(url, body);
    } catch (e) {
      lastErr = e;
      if (attempt >= retries || !_isRetryable(e)) break;
      if (typeof onRetry === "function") {
        try { onRetry(attempt + 1, retries); } catch (_) { /* noop */ }
      }
      // Linear backoff: 600ms, 1200ms, 1800ms…
      await new Promise((r) => setTimeout(r, backoffMs * (attempt + 1)));
    }
  }
  throw lastErr;
}

// Quick keep-warm probe — fire-and-forget on app mount + before login submit.
// Wakes the backend pod so the real auth request hits a warm worker.
export function warmupBackend() {
  try {
    return api.get("/health", { timeout: 8000 }).catch(() => null);
  } catch (_) {
    return null;
  }
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
